// Keep Fit App - API 服務層

import type { 
  FoodAnalysis, 
  FoodRecord, 
  DailySummary, 
  ApiResponse, 
  AnalyzeRequest,
  AnalyzeResponse 
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || '';

// 通用 fetch wrapper
async function fetchApi<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();
  
  if (!response.ok) {
    return { 
      success: false, 
      error: data.error || { code: 'UNKNOWN', message: 'Unknown error' } 
    };
  }

  return { success: true, data };
}

// 分析食物圖片
export async function analyzeFood(
  imageBase64: string, 
  mimeType: string,
  model?: string  // 可選：指定模型
): Promise<ApiResponse<AnalyzeResponse>> {
  const body: AnalyzeRequest = { 
    image: imageBase64, 
    mimeType,
    model  // 傳遞模型參數
  };
  
  return fetchApi<AnalyzeResponse>('/api/analyze', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// 獲取可用模型列表
export async function getAvailableModels(): Promise<string[]> {
  const response = await fetch(`${API_BASE}/api/models`);
  if (response.ok) {
    return response.json();
  }
  return ['gemini-2.0-flash', 'gemini-2.5-flash'];
}

// 設置偏好模型
export async function setPreferredModel(model: string): Promise<void> {
  localStorage.setItem('keep-fit-model', model);
}

// 獲取飲食記錄
export async function getRecords(params?: {
  date?: string;
  mealType?: string;
}): Promise<ApiResponse<FoodRecord[]>> {
  const query = new URLSearchParams();
  if (params?.date) query.set('date', params.date);
  if (params?.mealType) query.set('meal_type', params.mealType);
  
  const queryString = query.toString();
  const endpoint = `/api/records${queryString ? `?${queryString}` : ''}`;
  
  return fetchApi<FoodRecord[]>(endpoint);
}

// 建立飲食記錄
export async function createRecord(record: {
  mealType: string;
  analysis: FoodAnalysis;
  notes?: string;
}): Promise<ApiResponse<FoodRecord>> {
  return fetchApi<FoodRecord>('/api/records', {
    method: 'POST',
    body: JSON.stringify(record),
  });
}

// 更新飲食記錄
export async function updateRecord(
  id: string, 
  updates: Partial<FoodRecord>
): Promise<ApiResponse<FoodRecord>> {
  return fetchApi<FoodRecord>(`/api/records/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// 刪除飲食記錄
export async function deleteRecord(id: string): Promise<ApiResponse<void>> {
  return fetchApi<void>(`/api/records/${id}`, {
    method: 'DELETE',
  });
}

// 獲取統計數據
export async function getStats(period: 'day' | 'week' | 'month' = 'week'): Promise<ApiResponse<DailySummary[]>> {
  return fetchApi<DailySummary[]>(`/api/stats?period=${period}`);
}

// 健康檢查
export async function healthCheck(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
